import { useState } from 'react'
import { Check, X } from 'lucide-react'
import type { Creative, CreativeType, InstagramPostContent } from '../types'
import { ImagePickerModal } from './ImagePickerModal'
import type { MobileSelectedSection } from './mobile-selection-types'

const INPUT_CLASS =
  'border-line-bright bg-bg-3 text-paper placeholder:text-paper-faint focus:border-primary! w-full rounded-[10px] border px-3 py-2 text-[13px] outline-none transition-[border-color]'

const MAX_HASHTAGS = 5

interface MobileSectionEditorProps {
  creative: Creative
  selection: MobileSelectedSection
  onSave: (fields: Record<string, unknown>) => void
  onDiscard: () => void
}

export function MobileSectionEditor({
  creative,
  selection,
  onSave,
  onDiscard,
}: MobileSectionEditorProps) {
  let content: React.ReactNode = null

  if (selection.creativeType === 'instagram_post') {
    content = (
      <InstagramFieldEditor
        creative={creative as Creative<CreativeType.InstagramPost>}
        field={selection.sectionType as 'image' | 'caption' | 'hashtags'}
        onSave={onSave}
        onDiscard={onDiscard}
      />
    )
  }

  if (!content) return null

  return (
    <div className="rounded-[14px] border border-line bg-bg-2 shadow-[var(--shadow-card)]">
      {content}
    </div>
  )
}

// ─── Card wrapper with X / checkmark header ──────────────────────────────────

function EditorCard({
  onSave,
  onDiscard,
  children,
}: {
  onSave: () => void
  onDiscard: () => void
  children: React.ReactNode
}) {
  return (
    <>
      <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
        <button
          onClick={onDiscard}
          className="flex h-8 w-8 items-center justify-center rounded-full text-paper-dim transition-colors active:bg-bg-3"
          aria-label="Discard"
        >
          <X size={20} />
        </button>
        <button
          onClick={onSave}
          className="active:bg-acid-tint flex h-8 w-8 items-center justify-center rounded-full text-acid transition-colors"
          aria-label="Save"
        >
          <Check size={20} />
        </button>
      </div>
      <div className="px-4 pb-4">{children}</div>
    </>
  )
}

// ─── Instagram Field Editors (buffered) ──────────────────────────────────────

function InstagramFieldEditor({
  creative,
  field,
  onSave,
  onDiscard,
}: {
  creative: Creative<CreativeType.InstagramPost>
  field: 'image' | 'caption' | 'hashtags'
  onSave: (fields: Record<string, unknown>) => void
  onDiscard: () => void
}) {
  const content = creative.content as InstagramPostContent

  if (field === 'image') {
    return (
      <InstagramImageEditor
        creative={creative}
        content={content}
        onSave={onSave}
        onDiscard={onDiscard}
      />
    )
  }

  if (field === 'caption') {
    return <InstagramCaptionEditor content={content} onSave={onSave} onDiscard={onDiscard} />
  }

  if (field === 'hashtags') {
    return <InstagramHashtagsEditor content={content} onSave={onSave} onDiscard={onDiscard} />
  }

  return null
}

function InstagramCaptionEditor({
  content,
  onSave,
  onDiscard,
}: {
  content: InstagramPostContent
  onSave: (fields: Record<string, unknown>) => void
  onDiscard: () => void
}) {
  const [caption, setCaption] = useState(content.caption)

  return (
    <EditorCard onSave={() => onSave({ caption })} onDiscard={onDiscard}>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="mono text-[10px] tracking-[0.18em] text-paper-faint uppercase">
            Caption
          </label>
          <span className="mono text-[10px] text-paper-faint">{caption.length}/2200</span>
        </div>
        <textarea
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Write your caption..."
          rows={6}
          className={`${INPUT_CLASS} resize-none`}
          autoFocus
        />
      </div>
    </EditorCard>
  )
}

function InstagramHashtagsEditor({
  content,
  onSave,
  onDiscard,
}: {
  content: InstagramPostContent
  onSave: (fields: Record<string, unknown>) => void
  onDiscard: () => void
}) {
  const [hashtags, setHashtags] = useState(content.hashtags)
  const [hashtagInput, setHashtagInput] = useState('')
  const atLimit = hashtags.length >= MAX_HASHTAGS

  const addHashtag = (tag: string) => {
    if (atLimit) return
    const cleaned = tag.replace(/^#/, '').trim()
    if (cleaned && !hashtags.includes(cleaned)) {
      setHashtags(prev => [...prev, cleaned])
    }
  }

  const removeHashtag = (tag: string) => {
    setHashtags(prev => prev.filter(h => h !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addHashtag(hashtagInput)
      setHashtagInput('')
    }
    if (e.key === 'Backspace' && !hashtagInput && hashtags.length > 0) {
      removeHashtag(hashtags[hashtags.length - 1])
    }
  }

  return (
    <EditorCard onSave={() => onSave({ hashtags })} onDiscard={onDiscard}>
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <label className="mono text-[10px] tracking-[0.18em] text-paper-faint uppercase">
            Hashtags ({hashtags.length}/{MAX_HASHTAGS})
          </label>
          {atLimit && <span className="mono text-[10px] text-paper-faint">Limit reached</span>}
        </div>
        <div className="rounded-[10px] border border-line-bright bg-bg-3 p-2 transition-[border-color] focus-within:border-primary!">
          <div className="flex flex-wrap gap-1">
            {hashtags.map(tag => (
              <span
                key={tag}
                className="bg-acid-tint mono flex items-center gap-0.5 rounded-full border border-acid px-2 py-0.5 text-[11px] text-acid"
              >
                #{tag}
                <button onClick={() => removeHashtag(tag)} className="text-acid/60 hover:text-acid">
                  <X size={8} />
                </button>
              </span>
            ))}
            {!atLimit && (
              <input
                type="text"
                value={hashtagInput}
                onChange={e => setHashtagInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={hashtags.length === 0 ? 'Add hashtags...' : ''}
                className="min-w-[80px] flex-1 bg-transparent py-0.5 text-[11px] text-paper outline-none placeholder:text-paper-faint"
                autoFocus
              />
            )}
          </div>
        </div>
      </div>
    </EditorCard>
  )
}

function InstagramImageEditor({
  creative,
  content,
  onSave,
  onDiscard,
}: {
  creative: Creative<CreativeType.InstagramPost>
  content: InstagramPostContent
  onSave: (fields: Record<string, unknown>) => void
  onDiscard: () => void
}) {
  return (
    <ImagePickerModal
      creativeId={creative.id}
      currentImageUrl={content.imageUrl}
      currentAttachmentId={content.imageAttachmentId}
      open
      onOpenChange={open => {
        if (!open) onDiscard()
      }}
      onSelect={({ imageUrl, imageAttachmentId }) => {
        onSave({ imageUrl, imageAttachmentId })
      }}
    />
  )
}
